// 首页列表「首屏种子 pageSize」的共享纯函数 + 常量（零 import）。
//
// 页面 useResponsivePageSize 的首屏种子 pageSize = 上次持久化的 cols×rows（按列对齐、无残行），
// 无持久值时回退 fallback。页面第一个列表请求（recommend_feed/list 或 public_list）用的就是这个种子 size。
// 预取 boot 的 HomePlugin 必须算出同一个 size，否则 size 进 dedupKey → 与页面 body 不匹配 → 预取 miss。
//
// 两处同调一份：
//   - 主工程 hooks/useResponsivePageSize.ts 的种子计算。
//   - 预取 boot packages/sw-prefetch（经 esbuild @/ 别名内联）。
// 零依赖（入参用 string 宽松接收 localStorage 原值），避免 boot 独立类型检查牵连主工程类型图。

/** 持久化网格的 localStorage key 前缀。完整 key = 前缀 + storageKey（如 'home:desktop'）。 */
export const HOME_GRID_STORAGE_PREFIX = 'responsive-page-size:'

/** SSR / 未测量且无持久值时的种子 pageSize，足够大以撑满任意大屏首屏。与页面 fallback 一致。 */
export const HOME_PAGE_SIZE_FALLBACK = 40

/**
 * 解析持久化网格原值 → 首屏种子 pageSize。
 * rawStoredGrid：localStorage 里 `{cols,rows}` 的 JSON 字符串（或 null/缺失/非法）。
 * 合法（cols>0 && rows>0 && 有限）→ cols×rows；否则 → HOME_PAGE_SIZE_FALLBACK。
 */
export const computeHomeSeedPageSize = (
  rawStoredGrid: string | null | undefined
): number => {
  if (!rawStoredGrid) return HOME_PAGE_SIZE_FALLBACK
  try {
    const parsed = JSON.parse(rawStoredGrid) as {
      cols?: unknown
      rows?: unknown
    } | null
    const cols = Number(parsed?.cols)
    const rows = Number(parsed?.rows)
    if (
      cols > 0 &&
      rows > 0 &&
      Number.isFinite(cols) &&
      Number.isFinite(rows)
    ) {
      return cols * rows
    }
    return HOME_PAGE_SIZE_FALLBACK
  } catch {
    return HOME_PAGE_SIZE_FALLBACK
  }
}

/** 无持久化网格时的首屏图片预取张数兜底（约一屏），上限防大屏异常值抢带宽。 */
export const HOME_IMAGE_PREFETCH_FALLBACK = 12
export const HOME_IMAGE_PREFETCH_MAX = 24

/**
 * 解析持久化网格原值 → 首屏图片预取张数（SW 图片预取用）。
 * 持久化 rows 是 screenBuffer=2（备两屏）的行数，首屏可见约 rows/2 行，
 * 故张数 = cols × ceil(rows/2)——与页面 N1 提级口径（columns × prefetchRows ≈ 一屏）一致。
 * 无持久值回退 HOME_IMAGE_PREFETCH_FALLBACK，封顶 HOME_IMAGE_PREFETCH_MAX。
 */
export const computeHomeImagePrefetchCount = (
  rawStoredGrid: string | null | undefined
): number => {
  if (!rawStoredGrid) return HOME_IMAGE_PREFETCH_FALLBACK
  try {
    const parsed = JSON.parse(rawStoredGrid) as {
      cols?: unknown
      rows?: unknown
    } | null
    const cols = Number(parsed?.cols)
    const rows = Number(parsed?.rows)
    if (
      cols > 0 &&
      rows > 0 &&
      Number.isFinite(cols) &&
      Number.isFinite(rows)
    ) {
      return Math.min(HOME_IMAGE_PREFETCH_MAX, cols * Math.ceil(rows / 2))
    }
    return HOME_IMAGE_PREFETCH_FALLBACK
  } catch {
    return HOME_IMAGE_PREFETCH_FALLBACK
  }
}
